/* eslint-disable @typescript-eslint/no-explicit-any */
import { isInternalTraffic } from './internal'

declare global {
  interface Window {
    uetq: any[]
  }
}

/**
 * Always read window.uetq at call time.
 *
 * Before bat.js loads  → window.uetq is a plain array (events are queued).
 * After  bat.js loads  → window.uetq is replaced with a live UET object
 *                         whose .push() fires events immediately.
 *
 * Caching the reference at import time would point to the old dead array
 * after the swap, so we resolve it fresh on every call.
 */
export const getUetq = (): any[] => {
  if (isInternalTraffic()) return []
  if (typeof window !== 'undefined') {
    window.uetq = window.uetq || []
    return window.uetq
  }
  return []
}

type Action =
  | 'add_payment_info'
  | 'add_to_cart'
  | 'add_to_wishlist'
  | 'begin_checkout'
  | 'checkout_progress'
  | 'complete_kyc'
  | 'exception'
  | 'generate_lead'
  | 'login'
  | 'page_view'
  | 'purchase'
  | 'refund'
  | 'remove_from_cart'
  | 'screen_view'
  | 'search'
  | 'select_content'
  | 'set_checkout_option'
  | 'share'
  | 'sign_up'
  | 'timing_complete'
  | 'view_item'
  | 'view_item_list'
  | 'view_promotion'
  | 'view_search_results'
  // Segmented conversion events — Microsoft Ads goals
  | 'storage_purchase'
  | 'storage_complete_kyc'
  | 'storage_qualified_lead'
  | 'parking_monthly_purchase'
  | 'parking_monthly_complete_kyc'
  | 'parking_b2b_purchase'
  | 'parking_b2b_complete_kyc'
  | 'parking_b2b_qualified_lead'
  | 'parking_b2b_form_submit'
  | 'qualified_whatsapp_lead'
  | 'listing_whatsapp_click'
  // Soluciones Logísticas (3PL) — observed, never bid on; NOT in TIER_ROLLUP_MAP
  | 'logistics_lead'
  | 'logistics_contact_wa'
  // Supply-side (host) conversion events — separate from the demand-side
  // family so Microsoft Ads campaigns can optimize on supply intent without
  // contamination from renter leads.
  | 'supply_view'
  | 'supply_lead_start'
  | 'supply_lead'
  | 'supply_lead_individual'
  | 'supply_lead_business'
  | 'supply_lead_traditional'
  | 'supply_lead_calculate'
  | 'supply_lead_broker'
  | 'supply_lead_logistics'
  | 'supply_lead_invite'
  | 'supply_lead_add_post'
  | 'supply_listing_posted'

export const event = (
  action: Action,
  params?: { category?: string; label?: string; value?: string }
): void => {
  if (isInternalTraffic()) return
  getUetq().push('event', action, {
    event_category: params?.category,
    event_label: params?.label,
    event_value: params?.value
  })
}

const ms = { getUetq, event }

export default ms
